import BottomSheetHeader from '@components/BottomSheetHeader';
import Button from '@components/Button';
import Column from '@components/Column';
import Map from '@components/Map';
import Row from '@components/Row';
import Text from '@components/Text';
import TextUnderline from '@components/TextUnderline';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import useCopyShopAddress from '@hooks/shop/useCopyShopAddress';
import useShop from '@hooks/shop/useShop';
import useAppCustom from '@hooks/useAppCustom';
import Copy from '@icons/Copy';
import PhoneFill from '@icons/phone/PhoneFill';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { openExternalLink } from '@utils/nativeUtils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import WarningIcon from './WarningIcon';

interface Params {
    shopId: string;
    lat?: number;
    lng?: number;
}

declare module '@stackflow/config' {
    interface Register {
        ShopLocationBottomSheet: Params;
    }
}

const ShopLocationBottomSheet:ActivityComponentType<'ShopLocationBottomSheet'> = ({ params }) => {
    const {
        shopId, lat, lng,
    } = params;
    const { t } = useTranslation('shopLocation');
    const shop = useShop(shopId);
    const { pop, push } = useFlow();
    const copyAddress = useCopyShopAddress(shop?.address);
    const nameWords : string[] = useMemo(() => shop?.name.match(new RegExp(`.{1,${16}}`, 'g')) || [], [shop]);
    const handleOrderButtonClick = () => {
        pop();
        push('ItemsActivity', { shopId, type: 'ALL' });
    };
    const { usePhoneNumber } = useAppCustom().shop;
    return (
        <BottomSheet>
            <BottomSheetHeader/>
            <Column fullWidth align='center' spacing={20}>
                {shop && (
                    <Map center={{ lat: lat!, lng: lng! }}
                        myLocation={{ lat: lat!, lng: lng! }}
                        width='100%'
                        height={218}
                        markers={[
                            {
                                lat: lat!, lng: lng!, name: shop.name, id: shop.id, isMyShop: false,
                            },
                        ]} />
                )}
                <Column align='center' className={styles.contents}>
                    {nameWords.map((word, index) => (
                        <TextUnderline key={index}>
                            <Text size='3xl' fontWeight='bold' className={styles.name}>
                                {word}
                            </Text>
                        </TextUnderline>
                    ))}
                    <Text size='3xl'>{t('title')}</Text>
                </Column>
                <Column align='center'>
                    <Row spacing={8} onClick={copyAddress}
                        className={styles.addressContainer} >
                        <Text color={colors.gray6} size='lg' align='center' className={styles.address}>
                            {shop?.address}
                        </Text>
                        <Copy color={colors.gray6} size={16}/>
                    </Row>
                    {(shop?.phoneNumber && usePhoneNumber) && (
                        <Row spacing={8}
                            onClick={() => openExternalLink(`tel:${shop.phoneNumber}`)}>
                            <Text color={colors.gray6} size='lg'>
                                {shop.phoneNumber}
                            </Text>
                            <PhoneFill color={colors.gray6} size={16}/>
                        </Row>
                    )}
                </Column>
                <Column className={styles.footer} fullWidth
                    spacing={30} >
                    <Column align='center' className={styles.warning}
                        onClick={pop} >
                        <Row>
                            <WarningIcon/>
                            <Text>{t('warning.title')}</Text>
                        </Row>
                        <Text align='center'>{t('warning.description')}</Text>
                    </Column>
                    <Row fullWidth spacing={8}>
                        <Button variant='secondary' onClick={pop} css={css`flex: 1; padding: 0;`}>
                            {t('button.selectOtherShop')}
                        </Button>
                        <Button css={css`flex: 1; padding: 0;`}
                            disabled={shop?.orderStatus !== 'OPEN'}
                            onClick={handleOrderButtonClick} >
                            {shop?.orderStatus === 'OPEN' ? t('button.order') : t('button.preparing')}
                        </Button>
                    </Row>
                </Column>
            </Column>
        </BottomSheet>
    );
};

export default ShopLocationBottomSheet;
